import AddIcon from '@mui/icons-material/Add'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { List } from '@refinedev/mui'
import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'


import { useAppointments } from '../hooks'
import type { AppointmentStatus } from '../types'

import { AppointmentList } from './AppointmentList'
import CreateOSADialog from './CreateOSADialog'

import type { OsaEnvironment } from '@/shared/types'

export const OSASchedulingPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()

  // Get values from URL params with defaults
  const getStatusFromParams = (): AppointmentStatus => {
    const status = searchParams.get('status')
    if (status && ['all', 'new', 'dispatched', 'cancelled', 'completed'].includes(status)) {
      return status as AppointmentStatus
    }
    return 'new'
  }

  const getSortFieldFromParams = (): string => {
    const sortBy = searchParams.get('sortBy')
    if (sortBy) {
      return sortBy
    }
    // If no sortBy param in URL, default to 'scheduleStartTime' for initial page load
    return 'scheduleStartTime'
  }

  const getSortOrderFromParams = (): 'asc' | 'desc' => {
    const order = searchParams.get('sortOrder')
    return order === 'asc' ? 'asc' : 'desc'
  }

  const getEnvFromParams = (): OsaEnvironment | undefined => {
    const env = searchParams.get('env')
    if (env === 'test' || env === 'live') {
      return env as OsaEnvironment
    }
    // If no env param in URL, default to 'live' for initial page load
    if (!searchParams.has('env')) {
      return 'live'
    }
    return undefined
  }

  // Initialize state from URL params
  const [currentPage, setCurrentPage] = useState(() => {
    const page = searchParams.get('page')
    return page ? parseInt(page, 10) : 1
  })
  const [pageSize, setPageSize] = useState(() => {
    const size = searchParams.get('pageSize')
    return size ? parseInt(size, 10) : 10
  })
  const [activeStatus, setActiveStatus] = useState<AppointmentStatus>(getStatusFromParams)
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => searchParams.get('q') || ''
  )
  const [sortField, setSortField] = useState(getSortFieldFromParams)
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(getSortOrderFromParams)
  const [environment, setEnvironment] = useState<OsaEnvironment | undefined>(getEnvFromParams)
  const [typeFilter, setTypeFilter] = useState<string | undefined>(() => {
    const type = searchParams.get('type')
    return type && ['isfp', 'marketplace'].includes(type) ? type : undefined
  })
  const [subtypeFilter, setSubtypeFilter] = useState<string[] | undefined>(() => {
    const subtypes = searchParams.getAll('subtype').filter(s => ['heatpump', 'pv'].includes(s))
    return subtypes.length > 0 ? subtypes : undefined
  })
  const [funnelFilter, setFunnelFilter] = useState<string | undefined>(() => {
    const funnel = searchParams.get('funnel')
    return funnel && ['admin', 'event', 'scout', 'unknown'].includes(funnel) ? funnel : undefined
  })

  // Track last updated time
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  // Debounce search query to avoid too many API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      const hasSearchChanged = searchQuery !== debouncedSearchQuery
      setDebouncedSearchQuery(searchQuery)

      // Reset page if search query has changed
      if (hasSearchChanged) {
        setCurrentPage(1)
      }
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchQuery, debouncedSearchQuery])

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams()

    if (activeStatus !== 'new') params.set('status', activeStatus)
    if (currentPage > 1) params.set('page', currentPage.toString())
    if (pageSize !== 10) params.set('pageSize', pageSize.toString())
    if (debouncedSearchQuery) params.set('q', debouncedSearchQuery)
    if (sortField && sortField !== 'scheduleStartTime') params.set('sortBy', sortField)
    if (sortOrder !== 'desc') params.set('sortOrder', sortOrder)
    if (environment) params.set('env', environment)
    if (typeFilter) params.set('type', typeFilter)
    subtypeFilter?.forEach(subtype => params.append('subtype', subtype))
    if (funnelFilter) params.set('funnel', funnelFilter)

    setSearchParams(params, { replace: true })
  }, [
    activeStatus,
    currentPage,
    pageSize,
    debouncedSearchQuery,
    sortField,
    sortOrder,
    environment,
    typeFilter,
    subtypeFilter,
    funnelFilter,
    setSearchParams,
  ])

  const { data, isLoading, refetch, isRefetching, dataUpdatedAt } = useAppointments({
    pagination: {
      current: currentPage,
      pageSize,
    },
    filters: {
      status: activeStatus,
      query: debouncedSearchQuery || undefined,
      env: environment,
      type: typeFilter,
      subtype: subtypeFilter,
      funnel: funnelFilter,
    },
    sorters: {
      field: sortField,
      order: sortOrder,
    },
  })

  // Update lastUpdated timestamp whenever data is fetched
  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdated(new Date(dataUpdatedAt))
    }
  }, [dataUpdatedAt])

  // console.log('useAppointments result:', { data, isLoading }) // Debug log

  const appointments = data?.data || []
  const total = data?.total || 0

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }, [])

  const handleStatusChange = useCallback((status: AppointmentStatus) => {
    setActiveStatus(status)
    setCurrentPage(1) // Reset to first page when changing status
  }, [])

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSearchQuery(value)
    // Note: Page reset happens in the debounced search effect when debouncedSearchQuery changes
  }, [])

  const handleSortChange = useCallback((field: string, order: 'asc' | 'desc') => {
    setSortField(field)
    setSortOrder(order)
  }, [])

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  const handleEnvironmentChange = useCallback((env: OsaEnvironment | undefined) => {
    setEnvironment(env)
    setCurrentPage(1) // Reset to first page when changing environment
  }, [])

  const handleTypeFilterChange = useCallback((type: string | undefined) => {
    setTypeFilter(type)
    setCurrentPage(1) // Reset to first page when changing type filter
  }, [])

  const handleSubtypeFilterChange = useCallback((subtypes: string[] | undefined) => {
    setSubtypeFilter(subtypes)
    setCurrentPage(1) // Reset to first page when changing product filter
  }, [])

  const handleFunnelFilterChange = useCallback((funnel: string | undefined) => {
    setFunnelFilter(funnel)
    setCurrentPage(1) // Reset to first page when changing funnel filter
  }, [])

  const handleCreateOSA = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const handleCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  // Generate status text
  const getStatusText = useCallback(() => {
    const parts: (string | React.ReactElement)[] = []

    // Appointment count
    parts.push(`${total} appointments`)

    // Current status filter
    if (activeStatus !== 'all') {
      parts.push(
        <span key="status-filter">
          Filtered by Status:{' '}
          <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
            {activeStatus}
          </span>
        </span>
      )
    }

    // Environment filter
    if (environment) {
      parts.push(
        <span key="env-filter">
          Environment:{' '}
          <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
            {environment}
          </span>
        </span>
      )
    }

    // Type filter
    if (typeFilter) {
      const typeLabels: Record<string, string> = {
        isfp: 'iSFP',
        marketplace: 'Marketplace',
        private: 'Privat',
      }
      parts.push(
        <span key="type-filter">
          Type:{' '}
          <span style={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
            {typeLabels[typeFilter] ?? typeFilter}
          </span>
        </span>
      )
    }

    // Last updated
    const now = new Date()
    const diffMs = now.getTime() - lastUpdated.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)
    const diffMinutes = Math.floor(diffSeconds / 60)
    const diffHours = Math.floor(diffMinutes / 60)

    let timeText = ''
    if (diffSeconds < 60) {
      timeText = 'a few seconds ago'
    } else if (diffMinutes < 60) {
      timeText = `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    } else if (diffHours < 24) {
      timeText = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    } else {
      timeText = lastUpdated.toLocaleDateString()
    }
    parts.push(`Updated ${timeText}`)

    return parts.map((part, index) => (
      <span key={index}>
        {part}
        {index < parts.length - 1 && ' • '}
      </span>
    ))
  }, [total, activeStatus, environment, typeFilter, lastUpdated, theme.palette.primary.main])

  // Update status text every minute to keep "Updated X ago" current
  const [, setUpdateTrigger] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger(prev => prev + 1)
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [])

  return (
    <List
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon sx={{ color: theme.palette.primary.main }} />
          <span>OSA Scheduling</span>
        </Box>
      }
      headerButtons={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={handleRefresh}
              disabled={isRefetching}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <RefreshIcon
                sx={{
                  fontSize: 20,
                  animation: isRefetching ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOSA}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              },
            }}
          >
            Create OSA
          </Button>
        </Box>
      }
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        {/* Search Input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search customers, addresses, emails..."
            size="small"
            fullWidth
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Box>

        {/* Status Text */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.4,
            }}
          >
            {getStatusText()}
          </Typography>
        </Box>

        <Box sx={{ minHeight: 400 }}>
          <AppointmentList
            appointments={appointments}
            loading={isLoading || isRefetching}
            total={total}
            page={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onStatusChange={handleStatusChange}
            currentStatus={activeStatus}
            sortField={sortField}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            envFilter={environment}
            onEnvFilterChange={handleEnvironmentChange}
            typeFilter={typeFilter}
            onTypeFilterChange={handleTypeFilterChange}
            subtypeFilter={subtypeFilter}
            onSubtypeFilterChange={handleSubtypeFilterChange}
            funnelFilter={funnelFilter}
            onFunnelFilterChange={handleFunnelFilterChange}
          />
        </Box>
      </Paper>

      {/* Create OSA Dialog */}
      <CreateOSADialog open={createDialogOpen} onClose={handleCloseCreateDialog} />
    </List>
  )
}
